import type { Clock } from '../ports/Clock';
import type { IdGenerator } from '../ports/IdGenerator';
import type { RoutinePersistenceTransactions } from '../ports/RoutinePersistenceTransactions';
import { toContentData, type MissionContent } from '../../domain/routine/MissionContent';
import { DayMode, RoutineCategory } from '../../domain/routine/enums';
import type { RoutineStepTemplate, RoutineTemplate } from '../../domain/routine/models';
import type { ProfileId, RoutineStepTemplateId, RoutineTemplateId } from '../../domain/shared/ids';

interface SeedStep {
  readonly title: string;
  readonly category: RoutineStepTemplate['category'];
  readonly required: boolean;
  readonly essential?: boolean;
  readonly modes?: readonly DayMode[];
  readonly suggestedTime?: string;
  readonly estimatedDurationMinutes: number;
  readonly content: MissionContent;
}

const SEED_STEPS: readonly SeedStep[] = [
  {
    title: 'Réveil sans téléphone',
    category: RoutineCategory.mental,
    required: true,
    suggestedTime: '06:30',
    estimatedDurationMinutes: 5,
    content: {
      objective: 'Commencer la journée sans distraction.',
      instructions: [
        'Ne consulte pas les notifications.',
        'Lève-toi.',
        'Bois de l’eau.',
        'Respire calmement.',
      ],
    },
  },
  {
    title: 'Préparation mentale',
    category: RoutineCategory.mental,
    required: true,
    suggestedTime: '06:35',
    estimatedDurationMinutes: 10,
    content: {
      objective: 'Choisir ton état d’esprit avant de subir la journée.',
      instructions: [
        'Respire lentement pendant une minute.',
        'Identifie ta mission essentielle.',
        'Choisis l’attitude que tu veux incarner.',
      ],
    },
  },
  {
    title: 'Séance de sport',
    category: RoutineCategory.sport,
    required: true,
    suggestedTime: '07:00',
    estimatedDurationMinutes: 50,
    content: {
      objective: 'Activer le corps et renforcer la discipline.',
      instructions: [
        'Prépare ton équipement.',
        'Démarre la mission pour voir la séance détaillée du jour.',
        'Adapte, remplace ou ignore chaque exercice selon ton état.',
      ],
    },
  },
  {
    title: 'Douche et récupération',
    category: RoutineCategory.health,
    required: false,
    suggestedTime: '08:00',
    estimatedDurationMinutes: 15,
    content: {
      objective: 'Revenir au calme après l’effort.',
      instructions: ['Prends une douche.', 'Hydrate-toi.', 'Reviens à une respiration calme.'],
    },
  },
  {
    title: 'Planification de la journée',
    category: RoutineCategory.organization,
    required: true,
    suggestedTime: '08:15',
    estimatedDurationMinutes: 5,
    content: {
      objective: 'Donner une direction claire à la journée.',
      instructions: [
        'Choisis trois priorités maximum.',
        'Identifie la mission essentielle.',
        'Protège un créneau de travail profond.',
      ],
    },
  },
  {
    title: 'Travail profond',
    category: RoutineCategory.work,
    required: true,
    suggestedTime: '08:30',
    estimatedDurationMinutes: 90,
    content: {
      objective: 'Avancer sur la tâche ayant le plus d’impact.',
      instructions: [
        'Coupe les notifications.',
        'Prépare uniquement les outils nécessaires.',
        'Travaille sans interruption pendant le créneau prévu.',
      ],
    },
  },
  {
    title: 'Action de croissance',
    category: RoutineCategory.growth,
    required: false,
    estimatedDurationMinutes: 20,
    content: {
      objective: 'Construire une capacité ou une opportunité future.',
      instructions: [
        'Apprends, crée, prospecte ou améliore un système.',
        'Choisis une action concrète et mesurable.',
      ],
    },
  },
  {
    title: 'Relation consciente',
    category: RoutineCategory.relationship,
    required: false,
    estimatedDurationMinutes: 5,
    content: {
      objective: 'Entretenir un lien important sans pression.',
      instructions: [
        'Offre une attention sincère.',
        'Écoute ou envoie un message simple.',
        'Ne cherche pas de validation immédiate.',
      ],
    },
  },
  {
    title: 'Bilan du soir',
    category: RoutineCategory.journal,
    required: true,
    suggestedTime: '21:30',
    estimatedDurationMinutes: 10,
    content: {
      objective: 'Clore la journée avec lucidité.',
      instructions: [
        'Identifie une victoire.',
        'Note un apprentissage.',
        'Choisis la priorité de demain.',
      ],
      provisional: true,
      provisionalNote: 'Le journal complet appartient à L8.',
    },
  },
  {
    title: 'Rituel avant de dormir',
    category: RoutineCategory.mental,
    required: false,
    essential: true,
    estimatedDurationMinutes: 3,
    content: {
      objective: 'Préparer le repos et couper avec la journée.',
      instructions: [
        'Éloigne le téléphone.',
        'Respire lentement.',
        'Relâche la journée sans rumination.',
      ],
    },
  },
  {
    title: 'Boire de l’eau',
    category: RoutineCategory.health,
    required: true,
    modes: [DayMode.difficult],
    estimatedDurationMinutes: 1,
    content: {
      objective: 'Maintenir le geste minimal même dans un jour difficile.',
      instructions: ['Bois un verre d’eau.'],
    },
  },
  {
    title: 'Respiration consciente',
    category: RoutineCategory.mental,
    required: true,
    modes: [DayMode.difficult],
    estimatedDurationMinutes: 3,
    content: {
      objective: 'Retrouver un point d’ancrage stable.',
      instructions: ['Respire lentement pendant quelques minutes.', 'Ne cherche rien de plus.'],
    },
  },
];

export interface SeedMvpRoutineResult {
  readonly created: boolean;
}

export class SeedMvpRoutine {
  public constructor(
    private readonly transactions: RoutinePersistenceTransactions,
    private readonly clock: Clock,
    private readonly ids: IdGenerator,
  ) {}

  public async execute(profileId: ProfileId): Promise<SeedMvpRoutineResult> {
    const timestamp = this.clock.now().toISOString();
    const template: RoutineTemplate = {
      id: this.ids.generate<RoutineTemplateId>(),
      profileId,
      name: 'Ascension quotidienne',
      description: 'Socle initial des missions quotidiennes du MVP.',
      active: true,
      createdAt: timestamp,
      updatedAt: timestamp,
    };
    const steps = SEED_STEPS.map<RoutineStepTemplate>((step, index) => ({
      id: this.ids.generate<RoutineStepTemplateId>(),
      profileId,
      routineTemplateId: template.id,
      title: step.title,
      category: step.category,
      estimatedDurationMinutes: step.estimatedDurationMinutes,
      daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
      order: index,
      required: step.required,
      active: true,
      stepType: 'mission',
      content: toContentData(step.content),
      createdAt: timestamp,
      updatedAt: timestamp,
      ...(step.essential ? { essential: step.essential } : {}),
      ...(step.modes ? { modes: step.modes } : {}),
      ...(step.suggestedTime ? { suggestedTime: step.suggestedTime } : {}),
    }));

    return { created: await this.transactions.ensureTemplateWithSteps(template, steps) };
  }
}
